import {useEffect, useEffectEvent} from "react";
import {useLocation} from "react-router-dom";
import {getConsent, subscribeConsent} from "consent/consentStore";
import {useUser} from "UserProvider";
import {reportMetaRouteChange, syncMetaPixelWithConsent} from "./analytics";

/**
 * Keeps the Meta pixel in step with consent, with the signed-in account, and with the route
 * (kitecorp/transferify#375).
 *
 * A thin adapter by mandate (epic #26), the sibling of `useRedditPixel`: it holds no rules. Whether Meta
 * may run at all (site, consent, wording version, route, address) is `./metaPixel`'s decision, and the
 * order of start, identify and sign-up is {@link syncMetaPixelWithConsent}'s.
 *
 * Mounted on the app SHELL for the reason `useRedditPixel` is: it needs the session (for the account id)
 * and the router (for route changes), and must survive every route change.
 */
export function useMetaPixel(): void {
	const [user] = useUser();
	const location = useLocation();
	// The account's own uuid, NOT `user.id`, which is the Firebase Auth uid (api#1240): the server's
	// `external_id` and the sign-up's event id are this uuid. A dependency of its own because it arrives on
	// a later render than the session does.
	const accountId = user.accountId;

	useEffect(() => {
		const sync = () => {
			syncMetaPixelWithConsent(getConsent(), accountId);
		};
		sync();
		return subscribeConsent(sync);
	}, [accountId]);

	// Reads the latest account id without making it a dependency: a sign-in is not a page view.
	const reportRoute = useEffectEvent(() => {
		reportMetaRouteChange(getConsent(), accountId);
	});

	// Runs on mount too: whether that load's view was already counted is decided in `metaPixel`.
	useEffect(() => {
		reportRoute();
	}, [location.pathname]);
}
